import { Card, CardContent, Box } from '@material-ui/core'
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles'
import * as React from 'react'

import Typography from '../base/Typography'
import Tags, { TagInterface as Tag } from '../tags/Tags'

interface Visit {
  code: string
  package: string
  clientName: string
  address: string
}

export type VisitCardProps = {
  data: Visit
  duration: number
  tags: Tag[]
  customLayout: 'vertical' | 'horizontal'
  cardType:
    | 'officeCleaningJobs'
    | 'homeCleaningJobs'
    | 'Annual Leave'
    | 'Medical Leave'
    | 'Recommended'
}

const getLayoutSize = (
  customLayout: string,
  duration: number
): {
  width: number
  height: number
} => {
  if (customLayout === 'vertical') {
    const Vertical = {
      width: 101,
      height: 73 * duration
    }
    return Vertical
  }
  const Horizontal = {
    width: 118 * duration,
    height: 81
  }
  return Horizontal
}

const getPackageSize = (
  customLayout: string,
  duration: number
): {
  paddingBottom: number
  paddingRight: number
  width: number
} => {
  if (customLayout === 'vertical') {
    const Vertical = {
      paddingBottom: 6,
      paddingRight: 6,
      width: 101
    }
    return Vertical
  }
  const Horizontal = {
    paddingBottom: 8,
    paddingRight: 8,
    width: 118 * duration
  }
  return Horizontal
}

const getPackageStyle = (
  cardType: string
): {
  color: string
  background: string
} => {
  if (cardType === 'officeCleaningJobs') {
    const officeCleaningStyle = {
      color: '#2F80ED',
      background: '#C3E5F9'
    }
    return officeCleaningStyle
  }
  const homeCleaningJobsStyle = {
    color: '#219653',
    background: '#DFFAED'
  }
  return homeCleaningJobsStyle
}

const getCardContent = (
  customLayout: string
): {
  paddingLeft: number
  paddingTop: number
  marginRight: number
} => {
  if (customLayout === 'vertical') {
    const Vertical = {
      paddingLeft: 4,
      paddingTop: 6,
      marginRight: 6
    }
    return Vertical
  }
  const Horizontal = {
    paddingLeft: 7.11,
    paddingTop: 8,
    marginRight: 7.89
  }
  return Horizontal
}
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    recommend: (props: VisitCardProps) => ({
      background: '#EDF1F4',
      color: theme.palette.primary.main,
      borderRadius: 4,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      textAlign: 'center',
      '& .MuiCardContent-root:last-child': {
        padding: 0
      },
      ...getLayoutSize(props.customLayout, props.duration)
    }),
    medicalLeave: (props: VisitCardProps) => ({
      background: 'rgba(255, 230, 230, 0.45)',
      color: '#FF4444',
      borderRadius: 4,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      textAlign: 'center',
      '& .MuiCardContent-root:last-child': {
        padding: 0
      },
      ...getLayoutSize(props.customLayout, props.duration)
    }),
    annualLeave: (props: VisitCardProps) => ({
      background: 'rgba(187, 107, 217, 0.3)',
      color: '#9B51E0',
      borderRadius: 4,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      textAlign: 'center',
      '& .MuiCardContent-root:last-child': {
        padding: 0
      },
      ...getLayoutSize(props.customLayout, props.duration)
    }),
    homeCleaningJobs: (props: VisitCardProps) => ({
      background: '#DFFAED',
      color: '#222222',
      borderRadius: '4px 4px 0 0',
      ...getLayoutSize(props.customLayout, props.duration)
    }),
    officeCleaningJobs: (props: VisitCardProps) => ({
      background: '#C3E5F9',
      color: '#222222',
      borderRadius: '4px 4px 0 0',
      ...getLayoutSize(props.customLayout, props.duration)
    }),
    cardContentVists: (props: VisitCardProps) => ({
      ...getCardContent(props.customLayout),
      paddingBottom: 0,
      paddingRight: 0
    }),
    cardPackage: (props: VisitCardProps) => ({
      textAlign: 'right',
      borderRadius: '0px 0px 4px 4px',
      ...getPackageStyle(props.cardType),
      ...getPackageSize(props.customLayout, props.duration)
    }),
    boxPackage: {
      position: 'relative',
      bottom: 0
    }
  })
)
let nonJobCards = ''
const CalendarCards: React.FC<VisitCardProps> = (props) => {
  const classes = useStyles(props)
  const {
    data,
    duration,
    tags,
    cardType,
    customLayout,
    children,
    ...rest
  } = props
  const getStyle = (): string => {
    if (cardType === 'Recommended') {
      nonJobCards = cardType
      return classes.recommend
    }
    if (cardType === 'Annual Leave') {
      nonJobCards = cardType
      return classes.annualLeave
    }
    if (cardType === 'Medical Leave') {
      nonJobCards = cardType
      return classes.medicalLeave
    }
    if (cardType === 'officeCleaningJobs') {
      return classes.officeCleaningJobs
    }
    return classes.homeCleaningJobs
  }
  if (cardType === 'officeCleaningJobs' || cardType === 'homeCleaningJobs') {
    return (
      <Box className={getStyle()} alignItems="flex-end">
        <Card className={getStyle()} {...rest}>
          <CardContent className={classes.cardContentVists}>
            <Box display="flex" justifyContent="space-between">
              <Typography bodyVariants="smBold">{props.data.code}</Typography>
              {tags.map((tag, index) => (
                <Box key={`tag-${index}`}>
                  <Tags
                    label={tag.label}
                    customVariant={tag.variant}
                    sizes="xs"
                  />
                </Box>
              ))}
            </Box>
            <Typography bodyVariants="xsMedium">
              {props.data.clientName}
            </Typography>
            <Typography variant="caption">{props.data.address}</Typography>
          </CardContent>
        </Card>
        <Box className={classes.boxPackage}>
          <Typography bodyVariants="xsMedium" className={classes.cardPackage}>
            {props.data.package}
          </Typography>
        </Box>
      </Box>
    )
  } else {
    return (
      <Card className={getStyle()} {...rest}>
        <CardContent>
          <Typography variant="subtitle2">{nonJobCards}</Typography>
        </CardContent>
      </Card>
    )
  }
}

export default CalendarCards
